import { LoadingOutlined, SadIcon } from '@../../../components2/Icons';
import FilterFields from '@/components2/FilterFields';
import usePrefix from '@/hooks/usePrefix';
import { absoluteServerPath, CDN2, resolveConfigUrl } from '@helpers/api';
import { cacheFetch } from '@helpers/cacheFetch';
import { LoadScriptNext } from '@react-google-maps/api';
import { useMobxStores } from '@stores/index';
import { isServer } from '@/utils/isServer';
import { Affix, Button, Divider, Modal, Spin } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { NextRouter, useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import SearchComponent from '../../../components2/Search';
import { faMap, faSadCry } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { googleMapsApiKey } from '@/utils/googleMaps';
import SearchStore from '@/stores/SearchStore';
import { CloseOutlined } from '../../../components2/Icons';
import NoData from '@/components2/NoData';
import CardListC from '@/components2/CardListC';
import CardListB from '@/components2/CardListB';
import useABHook from '@/hooks/useABHook';
import Image from 'next/image';
import { modalType } from '@/components/Modal';
import { cleanupError, getSearchDates, getSearchDatesForSearch, storeNewDates } from '@/utils/searchUtils';
import { dateFormat } from '@/utils/tools';

const antIcon = <LoadingOutlined style={{ fontSize: 14 }} spin />;

const SearchMap = dynamic(() => import('@/components2/Map'), { ssr: false });
const Airports = dynamic(() => import('@/components2/Airports'), {
  ssr: true,
});

export async function getServerSideProps(ctx) {
  const UA = ctx.req.headers['user-agent'];
  const isMobile = Boolean(
    UA.match(
      /Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i
    )
  );
  const protocol = ctx.req.headers['x-forwarded-proto'] || 'http';
  const baseUrl = ctx.req ? `${protocol}://${ctx.req.headers.host}` : '';
  const edit: string = ctx.query.edit as string;

  const configInfo = await cacheFetch(resolveConfigUrl(baseUrl));
  const airports = await cacheFetch(
    `${absoluteServerPath(configInfo)}/api/Airport`
  );

  let currentAirport = null;
  for (let i = 0; i < airports?.result.length; i++) {
    if (airports.result[i].airport_initials == ctx.query?.initials) {
      currentAirport = airports.result[i];
      break;
    }
  }
  return {
    props: {
      airports,
      currentAirport,
      configInfo,
      edit: edit || null,
      deviceType: isMobile ? 'mobile' : 'desktop',
    },
  };
}

function Search({
  airports,
  currentAirport,
  configInfo,
  deviceType,
  edit,
  ...props
}) {
  const prefix = usePrefix();
  const {
    searchStore,
  }: {
    searchStore: SearchStore;
  } = useMobxStores();

  const router = useRouter();
  const [showSearch, setShowSearch] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [displayError, setDisplayError] = useState<string>("")
  const [isModalChangeDateVisible, setIsModalChangeDateVisible] =
    useState(false);

  // const [email, setEmail] = useState<string>();
  // const doSubscribe = async () => {
  //   if (email) {
  //     await searchStore.subscribe(currentAirport?.airport_id, email);
  //     modalType({https://app.clickup.com/t/868hn7xx6
  //       type: 'success',
  //       title: 'Success',
  //       okButtonProps: {
  //         className: 'bg-primary',
  //       },
  //       content: <p>Subscription for this was applied with success!</p>,
  //     });
  //   }
  // };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const searchResultText = searchStore.isLoading ? (
    <Spin indicator={antIcon} />
  ) : (
    <div className="grid grid-cols-3 sm:grid-cols-2 px-4">
      <div className="flex-col col-span-2 sm:col-auto">
        <p className="font-bold p-0 m-0">{currentAirport?.airport_name} ({currentAirport?.airport_initials})</p>
        <p>
          <small className="text-sm">
            for{' '}
            <strong>
              {dayjs(getSearchDates(router).checkin?.toString()).format(dateFormat)}
            </strong>{' '}
            to{' '}
            <strong>
              {dayjs(getSearchDates(router).checkout?.toString()).format(dateFormat)}
            </strong>
          </small>
        </p>
      </div>
      <Button
        type="default"
        size="middle"
        className="min-w-20 rounded-full justify-self-end block sm:hidden btn-orange font-bold mt-2"
        onClick={() => setShowSearch(!showSearch)}
      >
        {showSearch ? (
          <span className="text-base">
            <CloseOutlined />
          </span>
        ) : (
          'Edit'
        )}
      </Button>
    </div>
  );

  useEffect(() => {
    if (edit === 'true') {
      setIsModalChangeDateVisible(true);
    }
  }, [edit]);

  useEffect(() => {
    if (!getSearchDates(router).checkin || !getSearchDates(router).checkout) {
      router.push('/');
      return;
    }
    const sr = getSearchDatesForSearch(router)
    if (!isServer) {
      sessionStorage.setItem("checkin", sr.checkin)
      sessionStorage.setItem("checkout", sr.checkout)
    }
    searchStore.fetchSearchResult(null, sr).then(x=>{
      if (x.new_start_date || x.new_end_date) {
        storeNewDates({checkin: x.new_start_date, checkout: x.new_end_date})
      }
    });
    if (sr.error) {
      setDisplayError(sr.error)
      cleanupError()
    }
  }, [router, router.query, searchStore]);

  useEffect(() => {
    setTimeout(() => {
      setIsModalOpen(true);
    }, 2000);
  }, []);
  const searchType =
    currentAirport?.airport_display_type == 2 ? 'cruise' : 'airport';
  return (
    <div className="bg-white min-h-screen container mx-auto">
      <Head>
        <title>
          Search{' '}
          {!isServer && JSON.parse(sessionStorage.getItem('airport'))?.value}{' '}
          Facilities | On Air Parking
        </title>
        <meta name="robots" content="noindex" />
        <meta
          property="og:url"
          content="https://www.onairparking.com/reservation/search"
        />
        <meta
          property="og:title"
          content={`Search ${!isServer && JSON.parse(sessionStorage.getItem('airport'))?.value
            } | On Air Parking`}
        />
        <meta
          property="og:description"
          content={`On Air Parking provides the cheapest rates for parking near ${!isServer && JSON.parse(sessionStorage.getItem('airport'))?.value
            }. We guarantee to offer the lowest prices and there are free cancellations. Book now!`}
        />
        <meta
          name="title"
          content={`Search ${!isServer && JSON.parse(sessionStorage.getItem('airport'))?.value
            } | On Air Parking`}
        />
        <meta
          name="description"
          content={`On Air Parking provides the cheapest rates for parking near ${!isServer && JSON.parse(sessionStorage.getItem('airport'))?.value
            }. We guarantee to offer the lowest prices and there are free cancellations. Book now!`}
        />
        <link
          rel="preload"
          href={CDN2() + '/staticmyapp/bg-search-airport.svg'}
          as="image"
        />
        <link
          rel="preload"
          href={CDN2() + '/staticmyapp/cards-default-image.webp'}
          as="image"
        />
        <link
          rel="preload"
          href={CDN2() + '/staticmyapp/pvd-airport.webp'}
          as="image"
        />
        <link
          rel="preload"
          href={CDN2() + '/staticmyapp/default-image-3.webp'}
          as="image"
        />
      </Head>
      <div className="bg-white my-1 block sm:hidden border-b pb-1">{searchResultText}</div>
      <div
        className={`${showSearch ? 'block' : 'hidden'
          } sm:block sm:h-80 md:min-h-full md:h-auto relative bg-white my-4`}
      >
        <div className="flex flex-col md:mx-auto relative z-10 px-4">
          <SearchComponent
            searchType={searchType === 'cruise' ? 'cruise' : 'airport'}
            filterType="all"
            airports={airports}
            checkin={getSearchDates(router).checkin}
            checkout={getSearchDates(router).checkout}
            modalVisible={isModalChangeDateVisible}
            closeModal={() => setIsModalChangeDateVisible(false)}
            deviceType={deviceType}
          />
        </div>
      </div>
      {searchStore.searchResult?.result?.length == 0 ? (
        <>
          <Divider />
          <div className="px-4 md:px-0">
            <NoData
              imageNoData
              textNoData="Bummer! Nothing now, but new facilities coming soon!"
              bgImageWhite
              withSubscribe={true}
              airportId={currentAirport?.airport_id}
            />
            <Airports airports={airports} />
          </div>
        </>
      ) : (
        <>
          <CardListC
            currentAirport={currentAirport}
            configInfo={configInfo}
            deviceType={deviceType}
            props={props}
            checkin={getSearchDates(router).checkin}
            checkout={getSearchDates(router).checkout}
            key="cardlist"
          />
        </>
      )}

      <Modal
        centered
        visible={!!displayError}
        bodyStyle={{ backgroundColor: "#FFFFFF" }}
        destroyOnClose
        footer={false}
      >
        <div className="text-center px-4 space-y-4">
          <p className="w-12 h-12 mx-auto">
            <SadIcon
              fillColor="#e57715"
            />
          </p>
          <p className="font-bold text-lg">Sorry, something needs your attention before we continue.</p>
          <p className="text-onair-gray">
            {displayError}
          </p>
          <Button
            key="submit"
            onClick={() => {
              setDisplayError('');
              cleanupError();
            }}
            size="large"
            className="uppercase bg-primary hover:bg-primary hover:text-white hover:opacity-70 border-0 text-white rounded-full w-full"
          >
            Ok
          </Button>
        </div>
      </Modal>
    </div>
  );
}

Search.layout = 'SearchLayout';
export default observer(Search);
